/*
 * Copyright (c) 2025. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*
 * filtertool-regels.ts
 * Gegenereerd met Junie (AI), 05-10-2025
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Class-hiërarchie voor filtertoolregels met validatie, serialisatie en fabriek.
 */

///////////////////// Typen en constanten /////////////////////


/**
 * Ondersteunde regeltypes in de UI en in serialisatie.
 */
export const FiltertoolRegelTypes = ['planitemfunctie', 'planitemfunctionalias', 'planitemongekwalificeerd', 'planitempersoneel', 'personeelslabel', 'project', 'vrij'] as const;
export type FiltertoolregelType = typeof FiltertoolRegelTypes[number];

/**
 * Mapping van regeltype naar metadata voor weergave in de UI.
 * Bevat per type het label en een korte omschrijving.
 */
export const FiltertoolRegelTypeNamen: Record<FiltertoolregelType, { label: string; omschrijving: string, uitgeschakeld?: boolean }> = {
    project: {
        label: 'Project',
        omschrijving: 'Kies of specificeer projecten om resultaten te beperken tot die projecten.',
        uitgeschakeld: true
    },
    vrij: {
        label: 'Vrij',
        omschrijving: 'Vrije voorwaarde. Gebruik dit voor een eenvoudige, door jou benoemde regel.',
        uitgeschakeld: true
    },
    planitempersoneel: {
        label: 'Personeel',
        omschrijving: 'Filter op personeel binnen diensten. Toon alleen diensten met één van de geselecteerde medewerkers.'
    },
    personeelslabel: {
        label: 'Personeelslabel',
        omschrijving: 'Filter op personeelslabels. Matcht personeel of diensten waar iemand met één van deze labels op staat.'
    },
    planitemfunctie: {
        label: 'Functie',
        omschrijving: 'Filter op functie(s) binnen diensten. Toon alleen diensten met één van de geselecteerde functies.'
    },
    planitemfunctionalias: {
        label: 'Rijalias',
        omschrijving: 'Filter op rijalias binnen diensten. Toon alleen diensten met één van de opgegeven rijaliassen.'
    },
    planitemongekwalificeerd: {
        label: 'Ongekwalificeerd personeel',
        omschrijving: 'Toon alleen diensten met minstens één ingepland personeelslid dat de functie mist of waarvan de certificering op de dienstdatum verlopen is.'
    }
} as const;

///////////////////// Classes /////////////////////

/**
 * Abstracte parentclass voor alle filtertoolregels. Verzorgt basisvelden, validatie (default: geldig),
 * en serialisatie naar een plat object (tbv opslag).
 */
export abstract class Filtertoolregel {
    /** Unieke technische identificatie binnen een instantie. */
    id: string;
    /** Context waarop de regel betrekking heeft; fungeert als discriminator. */
    readonly context: FiltertoolregelType;
    /** Optionele, gebruikersvriendelijke naam of omschrijving. */
    naam?: string;

    protected constructor(context: FiltertoolregelType, init?: Partial<Filtertoolregel> & Record<string, any>) {
        this.context = context;
        this.id = init?.id ?? crypto.randomUUID();
        this.naam = init?.naam ?? undefined;
    }

    /**
     * Geeft aan of deze regel geldig is. Subclasses kunnen dit aanscherpen.
     */
    get valid(): boolean {
        return true;
    }

    /**
     * Exporteert de data van deze regel als plat object (geschikt voor JSON opslag).
     */
    toObject(): any {
        return {id: this.id, context: this.context, naam: this.naam};
    }

    /**
     * Let op: de creatie van concrete subclass-instanties is verplaatst naar filtertool-regel-factory.ts
     * Gebruik regelVanObject(obj) uit die module om vanuit platte objecten regels te maken.
     */
    // static fromObject(onbekend: any): FiltertoolRegel | null { /* verplaatst naar factory */ }

    /**
     * Let op: het aanmaken van nieuwe regels is verplaatst naar filtertool-regel-factory.ts
     * Gebruik nieuwRegel(type) uit die module om nieuwe regels te maken.
     */
    // static nieuw(type: FiltertoolRegelType): FiltertoolRegel { /* verplaatst naar factory */ }
}
